import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BkfcPlayer from './player/BkfcPlayer';
import { api, unwrap } from '../api';
import { imageUrl } from '../api/image';
import analytics from '../services/analytics';
import userDataService from '../services/UserDataService';
import { useAuthContext } from '../contexts/AuthContext';
import { useMediaState } from '../hooks/useMediaState';
import WatchButton from './common/WatchButton';
import { useWatchPointAutosave } from '../hooks/useWatchPointAutosave';
import './WatchTransition.css';

const OVERLAY_HIDE_MS = 3000;

const formatDate = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
};

/** The other episodes of this media's series (`GET /v1/series/{id}`), for the overlay strip. */
function useSeriesEpisodes(seriesId) {
  const [episodes, setEpisodes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setEpisodes([]);
    if (!seriesId) {
      return undefined;
    }
    unwrap(api.GET('/v1/series/{id}', { params: { path: { id: seriesId } } }))
      .then(({ data }) => {
        if (!cancelled) {
          setEpisodes(data?.episodes?.items || []);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [seriesId]);
  return episodes;
}

/** The media screen's card contract version: the app's LIB, so the edge serves both one object. */
const SCREEN_LIB = 7;

/**
 * The media's event, for the date and place under the title. `GET /v1/media/{id}` no longer carries
 * it (bkfc-cms #673; docs/routes/media.md "Where the dropped blocks live"): the media screen's header
 * names it (`media_header.props.event_card`, null when the media has no event) and
 * `GET /v1/events/{id}` has its date and place. Both are shared and edge-cached. Any failure leaves
 * the media's own date and no place, never an error.
 */
function useMediaEvent(mediaId) {
  const [event, setEvent] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setEvent(null);
    if (!mediaId) {
      return undefined;
    }
    unwrap(
      api.GET('/v1/screens/{key}', {
        params: { path: { key: 'media' }, query: { id: mediaId, lib: SCREEN_LIB } },
      }),
    )
      .then(({ data }) => {
        const card = data?.sections?.find(s => s.type === 'media_header')?.props?.event_card;
        return card
          ? unwrap(api.GET('/v1/events/{id}', { params: { path: { id: card.id } } }))
          : null;
      })
      .then(body => {
        if (!cancelled && body?.data) {
          setEvent(body.data);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mediaId]);
  return event;
}

/** The states whose button plays in-app (bkfc-cms media.md); ENDED plays too, as on mobile. */
const PLAYS = ['LIVE', 'REPLAY', 'WATCH_NOW', 'ENDED'];

/**
 * The watch page for one v1 media: `page` is the `GET /v1/media/{idOrSlug}` body's `data`
 * (`media`, `art`, `access`, `liveness`, `user`); the event comes from `useMediaEvent`. A media whose server `state` plays mounts the player
 * straight away, which gates and plays (`POST /v1/media/{id}/play`); any other state (off-app,
 * BUY NOW, a countdown, starting soon) shows its button over the poster and mounts nothing until
 * WATCH NOW is pressed. This page adds the overlay, the resume-point saves and the series episodes.
 */
const WatchPage = ({ page }) => {
  const { media, art } = page;
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const item = useMemo(() => ({ ...media, liveness: page.liveness }), [media, page.liveness]);
  const current = useMediaState(item);
  const [playing, setPlaying] = useState(() => !media.watch_on && PLAYS.includes(media.state));
  const isLive = current.state === 'LIVE';
  const episodes = useSeriesEpisodes(media.series?.id || media.series_id);
  const event = useMediaEvent(media.id);
  const title = media.title?.value || '';

  const videoRef = useRef(null);
  const bindingRef = useRef(null);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  // The overlay hides after a few seconds without input and comes back on any.
  useEffect(() => {
    let timer;
    const show = () => {
      clearTimeout(timer);
      setShowOverlay(true);
      timer = setTimeout(() => setShowOverlay(false), OVERLAY_HIDE_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart'];
    events.forEach(e => document.addEventListener(e, show));
    show();
    return () => {
      clearTimeout(timer);
      events.forEach(e => document.removeEventListener(e, show));
    };
  }, []);

  const saveNow = useWatchPointAutosave({
    enabled: Boolean(isAuthenticated && media.id),
    isLive,
    getCurrentTime: () => videoRef.current?.currentTime || 0,
    onSave: (time, opts = {}) =>
      userDataService.saveVideoWatchPoint(media.id, time, {
        durationS: videoRef.current?.duration,
        keepalive: opts.keepalive,
      }),
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!playerLoaded || !video) {
      return undefined;
    }
    const handlePause = () => saveNow(video.currentTime);
    // Natural end: completion is position_s = duration_s (bkfc-cms #649), which moves the title
    // to watch-again. position_s 0 means "clear the resume point" and lands in neither rail.
    // saveNow carries the live gate and the duration; an unknown duration saves nothing.
    const handleEnded = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        saveNow(video.duration);
      }
    };
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [playerLoaded, saveNow, media.id, isLive]);

  useEffect(() => () => bindingRef.current?.(), []);

  const handlePlayerReady = useCallback(
    ({ video, player }) => {
      videoRef.current = video;
      bindingRef.current?.();
      bindingRef.current = analytics.bindPlayer(
        player,
        {
          content_id: media.id,
          content_title: title,
          content_type: media.kind,
          duration: media.duration_s,
        },
        { targetSelector: '#pdp-player' },
      );
      setPlayerLoaded(true);
    },
    [media.id, media.kind, media.duration_s, title],
  );

  const meta = [
    formatDate(event?.event_date || media.starts_at),
    event?.location || event?.venue_name,
  ].filter(Boolean);
  const poster = imageUrl(art?.landscape, '16x9', 1920) || undefined;

  return (
    <div className="watch-container">
      <div id="pdp-player">
        {playing ? (
          <BkfcPlayer
            mediaId={media.id}
            media={media}
            live={isLive}
            poster={poster}
            autostart
            controls
            muted={false}
            onReady={handlePlayerReady}
          />
        ) : (
          <div
            className="relative w-full h-full min-h-[60vh] bg-black bg-center bg-cover flex items-end justify-center pb-24"
            style={poster ? { backgroundImage: `url(${poster})` } : undefined}
          >
            <div className="relative z-[60] pointer-events-auto">
              <WatchButton item={item} onPlay={() => setPlaying(true)} />
            </div>
          </div>
        )}
      </div>

      <div
        className={`absolute inset-0 z-50 transition-opacity duration-300 pointer-events-none ${
          showOverlay ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/85 via-black/55 to-transparent p-6 pointer-events-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/20 bg-black/35 text-white/90 hover:text-accent hover:border-accent/50 font-display uppercase tracking-wide text-xs md:text-sm"
          >
            &larr; Back
          </button>
          <h1 className="mt-4 text-2xl md:text-3xl font-display uppercase tracking-wide text-white mb-2">
            {title}
          </h1>
          {meta.length > 0 && <p className="text-white/80 text-sm font-body">{meta.join(' • ')}</p>}
        </div>

        {episodes.length > 1 && (
          <nav
            aria-label="Episodes"
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-6 pointer-events-auto"
          >
            <h2 className="text-white font-display uppercase tracking-wide text-sm mb-2">
              {media.series?.title || 'Episodes'}
            </h2>
            <ul className="flex gap-3 overflow-x-auto">
              {episodes.map(ep => (
                <li key={ep.id} className="shrink-0">
                  <Link
                    to={`/watch/${ep.slug || ep.id}`}
                    aria-current={ep.id === media.id ? 'true' : undefined}
                    className={`block px-3 py-2 rounded border text-sm ${
                      ep.id === media.id
                        ? 'border-accent text-accent'
                        : 'border-white/20 text-white/90 hover:border-white/60'
                    }`}
                  >
                    {ep.title?.value || ep.slug}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
};

export default WatchPage;
